<template>
	<div id="sentiment_market_hot_trend" :class="{ 'site-dark-mode': theme === 'dark' }">
		<!-- Top date selection row -->
		<div class="row align-items-center mb-3">
			<div class="col-auto" style="white-space: nowrap;">
				<b-form-datepicker id="datepicker" size="sm" :date-disabled-fn="dateDisabled" @context="onDatePicked" v-model="date1"
				class="mb-1" today-button close-button locale="zh" style="width: 240px; white-space: nowrap;"></b-form-datepicker>
			</div>
			<div class="col-auto">
				<b-form-checkbox
				size="sm"
				id="checkbox_auto_refresh"
				v-model.number="auto_refresh"
				name="checkbox_auto_refresh"
				value="1"
				unchecked-value="0"
				>
				自动刷新
				</b-form-checkbox>
			</div>
		</div>

		<!-- Main layout: Left sidebar cards, Right chart canvas -->
		<div class="sentiment-layout-container">
			<!-- Left side navigation tabs -->
			<div class="sentiment-sidebar">
				<!-- Tab 1: 日内情绪 -->
				<div :class="['trend-tab-card', { active: activeTab === 'sentiment' }]" @mouseenter="activeTab = 'sentiment'" @click="activeTab = 'sentiment'">
					<div class="trend-tab-card-header">
						<span class="trend-tab-icon">📈</span>
						<span class="trend-tab-title">日内情绪</span>
					</div>
					<div class="trend-tab-card-body">
						<div class="d-flex flex-wrap w-100" style="gap: 2px 6px;">
							<div>
								<span class="trend-tab-label">主要:</span>
								<strong :style="{ color: getPctColor(currentMainPct) }">{{ currentMainPct !== '-' ? currentMainPct + '%' : '-' }}</strong>
							</div>
							<div>
								<span class="trend-tab-label">敏感:</span>
								<strong :style="{ color: getPctColor(currentSensPct) }">{{ currentSensPct !== '-' ? currentSensPct + '%' : '-' }}</strong>
							</div>
						</div>
						<div class="w-100 mt-1 pt-1" style="border-top: 1px dashed rgba(128, 128, 128, 0.2); display: flex; flex-wrap: wrap; gap: 2px 6px;">
							<div>
								<span class="trend-tab-label text-danger">自涨:</span>
								<strong class="text-danger">{{ currentZrztNum }}</strong>
							</div>
							<div>
								<span class="trend-tab-label text-warning">炸板:</span>
								<strong class="text-warning">{{ currentZbNum }}</strong>
							</div>
							<div>
								<span class="trend-tab-label text-success">跌停:</span>
								<strong class="text-success">{{ currentDownlimitNum }}</strong>
							</div>
						</div>
					</div>
				</div>

				<!-- Tab 2: 涨跌分布 -->
				<div :class="['trend-tab-card', { active: activeTab === 'distribution' }]" @mouseenter="activeTab = 'distribution'" @click="activeTab = 'distribution'">
					<div class="trend-tab-card-header">
						<span class="trend-tab-icon">📊</span>
						<span class="trend-tab-title">涨跌分布</span>
					</div>
					<div class="trend-tab-card-body">
						<div class="w-100">
							<span class="trend-tab-label text-danger">上涨:</span>
							<strong class="text-danger">
								{{ currentUpNum }}<span style="font-size: 11px; font-weight: normal; opacity: 0.85;">({{ currentUpRatio !== '-' ? currentUpRatio + '%' : '-' }})</span>
							</strong>
						</div>
						<div class="w-100 mt-1">
							<span class="trend-tab-label text-success">下跌:</span>
							<strong class="text-success">
								{{ currentDownNum }}<span style="font-size: 11px; font-weight: normal; opacity: 0.85;">({{ currentDownRatio !== '-' ? currentDownRatio + '%' : '-' }})</span>
							</strong>
						</div>
					</div>
				</div>

				<!-- Tab 3: 涨跌数 -->
				<div :class="['trend-tab-card', { active: activeTab === 'counts' }]" @mouseenter="activeTab = 'counts'" @click="activeTab = 'counts'">
					<div class="trend-tab-card-header">
						<span class="trend-tab-icon">🔄</span>
						<span class="trend-tab-title">涨跌数</span>
					</div>
					<div class="trend-tab-card-body">
						<span class="trend-tab-label">均涨幅:</span>
						<strong :style="{ color: getPctColor(currentAvgPct) }">{{ currentAvgPct !== '-' ? currentAvgPct + '%' : '-' }}</strong>
					</div>
				</div>
			</div>

			<!-- Right side chart area -->
			<div class="sentiment-chart-area">
				<v-chart 
					class="chart_hot_trend" 
					:options="chartOptions" 
					:theme="echartsTheme" 
					:key="activeTab + '_' + echartsTheme" 
				/>

				<!-- Scrubber Container with fixed height to prevent jitter -->
				<div class="sentiment-scrubber-container">
					<div v-if="activeTab === 'distribution' && categoryData.length > 0" class="px-2">
						<div class="d-flex justify-content-between align-items-center mb-1">
							<div class="d-flex align-items-center">
								<b-button 
									size="sm" 
									variant="danger" 
									class="mr-2 py-0 px-2 d-flex align-items-center justify-content-center"
									@click="togglePlay"
									style="height: 22px; font-size: 11px; border-radius: 4px; font-weight: bold; min-width: 60px;"
								>
									<span v-if="isPlaying">⏸ 暂停</span>
									<span v-else>▶ 播放</span>
								</b-button>
								<span class="small text-muted">当前分布时间: <strong class="text-primary">{{ currentTime || '加载中...' }}</strong></span>
							</div>
							<span class="small text-muted">← 拖动滑块或点击播放查看日内变化 →</span>
						</div>
						<b-form-input 
							id="time-scrubber" 
							v-model.number="selectedIndex" 
							type="range" 
							min="0" 
							:max="maxAvailableIndex" 
							step="1"
							class="custom-slider"
							@mousedown="stopPlay"
							@touchstart="stopPlay"
						></b-form-input>
					</div>
				</div>
			</div>
		</div>

		<!-- Footer usage toggle -->
		<div class="text-right mt-3 mb-1">
			<b-link v-b-toggle.hot-trend-usage class="text-muted small">使用说明 <b-icon icon="info-circle" /></b-link>
		</div>
		<b-collapse id="hot-trend-usage">
			<b-alert show variant="light" class="small py-2 mb-2">
				<ul class="mb-0">
					<li>按照日内分时来量化市场情绪，结合按天情绪来使用</li>
					<li>敏感情绪体现高位风险，比如瀑布出现</li>
					<li>敏感情绪体现早盘机会，比如早盘越界</li>
					<li>普通情绪体现综合市场的冷暖</li>
					<li>重点在于
						<ul>
							<li>A:按天情绪在过热时候的第一次分歧减仓，第二次分歧空仓</li>
							<li>B:按天情绪在过冷时候市场的企稳</li>
							<li>C:节点处试错，特别是下面空间板低位共振</li>
							<li>D:按天趋势形成向上后推仓位，早盘情绪有体现</li>
						</ul>
					</li>
				</ul>
			</b-alert>
		</b-collapse>
	</div>
</template>

<script>
	import ECharts from 'vue-echarts'
	import axios from 'axios'

	import {
		request_zzquant
	} from "network/request.js"

	const upColor = '#ec0000';
	const upBorderColor = '#8A0000';
	const downColor = '#00da3c';
	const downBorderColor = '#008F28';

	function generateTradingMinutes() {
		const minutes = [];
		// 09:30 to 11:30
		let cur = new Date(2020, 0, 1, 9, 30, 0);
		const endMorning = new Date(2020, 0, 1, 11, 30, 0);
		while (cur <= endMorning) {
			const h = String(cur.getHours()).padStart(2, '0');
			const m = String(cur.getMinutes()).padStart(2, '0');
			minutes.push(`${h}:${m}:00`);
			cur.setMinutes(cur.getMinutes() + 1);
		}
		// 13:00 to 15:00
		cur = new Date(2020, 0, 1, 13, 0, 0);
		const endAfternoon = new Date(2020, 0, 1, 15, 0, 0);
		while (cur <= endAfternoon) {
			const h = String(cur.getHours()).padStart(2, '0');
			const m = String(cur.getMinutes()).padStart(2, '0');
			minutes.push(`${h}:${m}:00`);
			cur.setMinutes(cur.getMinutes() + 1);
		}
		return minutes;
	}
	const STANDARD_TRADING_MINUTES = generateTradingMinutes();

	export default {
		props: {
			title: {
				type: String,
				default: "日内情绪热度"
			},
			subText: {
				type: String,
				default: "分时热度"
			},
			zoom_start: {
				type:  [Number, String],
				default: 70,
			},
			zoom_end: {
				type: Number,
				default: 100,
			}
		},
		mounted() {
			request_zzquant({url:'/open/trade/days'}).then(
				res => {
					let days = res.data.data;
					if (days && days.length > 0) {
						this.date1 = days[days.length-1];
					}
					this.isDaysLoaded = true;
					this.refresh();
				}
			).catch(err => {
				console.error("Failed to fetch trade days:", err);
				this.isDaysLoaded = true;
				this.refresh();
			});
			this.timer = setInterval(() => {  //创建定时器 30s
				this.refresh_intervel();
			}, 30000);
			try{ const t = localStorage.getItem('site.theme'); if(t){ this.theme = t } }catch(e){ console.warn('theme read failed', e) }
			window.addEventListener('site-theme-changed', this.onThemeChanged)
		},
		components: {
			'v-chart': ECharts
		},
		data() {
			const now = new Date();
			const today = now.getFullYear() + '-' + (now.getMonth() + 1) + '-' + now.getDate();
			return {
				theme:'light',
				hot_y_axis:{v_min:-5,v_max:6},
				auto_refresh:0,
				date1: today,
				isDaysLoaded: false,
				lastRefreshedDate: null,
				lastDataIndex: -1,
				timer: null,

				// State management for navigation & scrubbing
				activeTab: 'sentiment', // 'sentiment' | 'distribution' | 'counts'
				selectedIndex: -1,

				// Parsed datasets
				categoryData: [],
				pct: [],
				pct_zrlb: [],
				zrzt_num: [],
				zb_num: [],
				avg_pct: [],
				downlimit_num: [],
				up_num: [],
				down_num: [],
				distributeTrendData: [],

				// Playback control state
				isPlaying: false,
				playTimer: null
			}
		},
		computed:{
			echartsTheme(){ return this.theme==='dark' ? 'site-dark' : null },
			maxAvailableIndex() {
				return this.lastDataIndex >= 0 ? this.lastDataIndex : Math.max(0, this.categoryData.length - 1);
			},
			
			// Dynamic left panel subtitle values
			currentMainPct() {
				if (this.selectedIndex < 0 || !this.pct.length) return '-';
				const val = this.pct[this.selectedIndex];
				return val !== null && val !== undefined ? val : '-';
			},
			currentSensPct() {
				if (this.selectedIndex < 0 || !this.pct_zrlb.length) return '-';
				const val = this.pct_zrlb[this.selectedIndex];
				return val !== null && val !== undefined ? val : '-';
			},
			currentUpNum() {
				if (this.selectedIndex < 0 || !this.up_num.length) return '-';
				const val = this.up_num[this.selectedIndex];
				return val !== null && val !== undefined ? val : '-';
			},
			currentDownNum() {
				if (this.selectedIndex < 0 || !this.down_num.length) return '-';
				const val = this.down_num[this.selectedIndex];
				return val !== null && val !== undefined ? val : '-';
			},
			currentZrztNum() {
				if (this.selectedIndex < 0 || !this.zrzt_num.length) return '-';
				const val = this.zrzt_num[this.selectedIndex];
				return val !== null && val !== undefined ? val : '-';
			},
			currentZbNum() {
				if (this.selectedIndex < 0 || !this.zb_num.length) return '-';
				const val = this.zb_num[this.selectedIndex];
				return val !== null && val !== undefined ? val : '-';
			},
			currentDownlimitNum() {
				if (this.selectedIndex < 0 || !this.downlimit_num.length) return '-';
				const val = this.downlimit_num[this.selectedIndex];
				return val !== null && val !== undefined ? val : '-';
			},
			currentAvgPct() {
				if (this.selectedIndex < 0 || !this.avg_pct.length) return '-';
				const val = this.avg_pct[this.selectedIndex];
				return val !== null && val !== undefined ? val : '-';
			},
			currentTime() {
				if (this.selectedIndex < 0 || !this.categoryData.length) return '';
				return this.categoryData[this.selectedIndex] || '';
			},
			currentUpRatio() {
				if (this.selectedIndex < 0 || !this.up_num.length || !this.down_num.length) return '-';
				const up = this.up_num[this.selectedIndex];
				const down = this.down_num[this.selectedIndex];
				if (up === null || up === undefined || down === null || down === undefined) return '-';
				
				const dist = (this.distributeTrendData && this.distributeTrendData[this.selectedIndex]) || {};
				const flat = dist.eq_0_num !== undefined && dist.eq_0_num !== null ? dist.eq_0_num : 0;
				
				const total = up + down + flat;
				if (total === 0) return '0.0';
				return ((up / total) * 100).toFixed(1);
			},
			currentDownRatio() {
				if (this.selectedIndex < 0 || !this.up_num.length || !this.down_num.length) return '-';
				const up = this.up_num[this.selectedIndex];
				const down = this.down_num[this.selectedIndex];
				if (up === null || up === undefined || down === null || down === undefined) return '-';
				
				const dist = (this.distributeTrendData && this.distributeTrendData[this.selectedIndex]) || {};
				const flat = dist.eq_0_num !== undefined && dist.eq_0_num !== null ? dist.eq_0_num : 0;
				
				const total = up + down + flat;
				if (total === 0) return '0.0';
				return ((down / total) * 100).toFixed(1);
			},

			// Dynamic ECharts configurations
			chartOptions() {
				if (this.activeTab === 'sentiment') {
					return this.getSentimentOptions();
				} else if (this.activeTab === 'distribution') {
					return this.getDistributionOptions();
				} else if (this.activeTab === 'counts') {
					return this.getCountsOptions();
				}
				return {};
			}
		},
		watch: {
			categoryData(newVal) {
				if (newVal && newVal.length > 0) {
					this.selectedIndex = this.maxAvailableIndex;
				} else {
					this.selectedIndex = -1;
				}
			},
			activeTab(newTab) {
				if (newTab === 'distribution' && this.categoryData.length > 0) {
					this.selectedIndex = this.maxAvailableIndex;
				}
			}
		},
		methods: {
			togglePlay() {
				if (this.isPlaying) {
					this.stopPlay();
				} else {
					this.startPlay();
				}
			},
			startPlay() {
				if (this.categoryData.length === 0) return;
				
				// 自动切换到涨跌分布 Tab 页
				if (this.activeTab !== 'distribution') {
					this.activeTab = 'distribution';
				}
				
				// 如果已经在最右侧（结束点），重新从最左侧（0）开始播放
				if (this.selectedIndex >= this.maxAvailableIndex || this.selectedIndex < 0) {
					this.selectedIndex = 0;
				}
				
				this.isPlaying = true;
				
				if (this.playTimer) {
					clearInterval(this.playTimer);
				}
				
				// 设定 15ms 的播放间隔，逐步从左往右滑动
				this.playTimer = setInterval(() => {
					if (this.selectedIndex < this.maxAvailableIndex) {
						this.selectedIndex = Math.min(this.selectedIndex + 5, this.maxAvailableIndex);
					} else {
						this.stopPlay();
					}
				}, 30);
			},
			stopPlay() {
				this.isPlaying = false;
				if (this.playTimer) {
					clearInterval(this.playTimer);
					this.playTimer = null;
				}
			},
			getPctColor(val) {
				if (val === '-' || val === null || val === undefined) return '#999999';
				const num = parseFloat(val);
				if (isNaN(num)) return '#999999';
				if (num > 0) return '#ef4444'; // Red
				if (num < 0) return '#10b981'; // Green
				return '#999999'; // Grey
			},
			clearTimer() {
				clearInterval(this.timer);
				this.timer = null;
			},
			refresh_intervel(){
				if(Number(this.auto_refresh)){
					let  myDate = new Date();
					let day = myDate.getDay()
					let hour = myDate.getHours();
					if (0<day && day<6 && 9<=hour && hour<=15){
						this.refresh();
					}else{
						console.log("trend 非交易时间不刷新");
					}
				}
			},
			onDatePicked(ctx) {
				if (!this.isDaysLoaded) return;
				if (ctx.selectedYMD && ctx.selectedYMD !== this.lastRefreshedDate) {
					this.date1 = ctx.selectedYMD;
					this.refresh();
				}
			},
			dateDisabled(ymd, date) {
				const weekday = date.getDay();
				const time = date.getTime()
				const now = new Date();
				const now_time = now.getTime();
				if (weekday === 0 || weekday === 6) {
					return true;
				}
				if (time >= now_time) {
					return true;
				}
				return false
			},
			refresh() {
				let date = this.date1;
				this.lastRefreshedDate = date;
				axios.all([
						request_zzquant({
							'url': 'v3/api/sentiment/trend/0?date1=' + date
						}),
						request_zzquant({
							'url': 'v3/api/sentiment/trend/1?date1=' + date
						}),
						request_zzquant({
							'url': 'v3/sentiment/distribute/trend?date1=' + date
						}),
					])
					.then(axios.spread((resp1, resp2, resp3) => {
						let splitDataItem = this.splitData(resp1['data']['data'], resp2['data']['data'], resp3['data']['data']);
						
						// Store all arrays reactively
						this.categoryData = splitDataItem.categoryData;
						this.pct = splitDataItem.pct;
						this.pct_zrlb = splitDataItem.pct_zrlb;
						this.zrzt_num = splitDataItem.zrzt_num;
						this.zb_num = splitDataItem.zb_num;
						this.avg_pct = splitDataItem.avg_pct;
						this.downlimit_num = splitDataItem.downlimit_num;
						this.up_num = splitDataItem.up_num;
						this.down_num = splitDataItem.down_num;
						this.distributeTrendData = splitDataItem.distributeTrendData;

						// Synchronize scrubber index to default to the latest time
						this.selectedIndex = this.maxAvailableIndex;
					}))
					.catch(err => {
						console.log(err);
					})
			},
            splitData(rawData, rowData2, rowData3) {
                const categoryData = [...STANDARD_TRADING_MINUTES];
                const pct = [];
                const pct_zrlb = [];
                const avg_pct = [];
                const downlimit_num = [];
                const up_num = [];
                const down_num = [];
                const zb_num = [];
                const zrzt_num = [];
                const distributeTrendData = [];

                // Find the latest time in rawData
                let maxTime = "";
                if (Array.isArray(rawData)) {
                    rawData.forEach(item => {
                        if (item && item[0] && item[0] > maxTime) {
                            maxTime = item[0];
                        }
                    });
                }

                // Fallback search in rowData2
                if (!maxTime && Array.isArray(rowData2)) {
                    rowData2.forEach(item => {
                        if (item && item[0] && item[0] > maxTime) {
                            maxTime = item[0];
                        }
                    });
                }

                // Map rawData
                const rawMap = {};
                if (Array.isArray(rawData)) {
                    rawData.forEach(item => {
                        if (item && item.length >= 2) {
                            rawMap[item[0]] = item[1];
                        }
                    });
                }

                // Map rowData2
                const rawMap2 = {};
                if (Array.isArray(rowData2)) {
                    rowData2.forEach(item => {
                        if (item && item.length >= 2) {
                            rawMap2[item[0]] = item[1];
                        }
                    });
                }

                // Map rowData3 (distributeData)
                const distributeData = rowData3 && Array.isArray(rowData3) ? rowData3 : [];
                const distMap = {};
                distributeData.forEach(item => {
                    if (item && item.minute) {
                        let key = item.minute;
                        if (key.length === 5) {
                            key += ":00";
                        }
                        distMap[key] = item;
                    }
                });

                let lastPct = null;
                let lastPctZrlb = null;
                let lastDistItem = null;
                let maxTimeIndex = -1;

                categoryData.forEach((t, idx) => {
                    if (t <= maxTime) {
                        maxTimeIndex = idx;

                        // pct
                        if (rawMap[t] !== undefined) {
                            lastPct = rawMap[t];
                        }
                        pct.push(lastPct);

                        // pct_zrlb
                        if (rawMap2[t] !== undefined) {
                            lastPctZrlb = rawMap2[t];
                        }
                        pct_zrlb.push(lastPctZrlb);

                        // distribute items
                        const timeKey = t.substring(0, 5);
                        const distItem = distMap[t] || distMap[timeKey + ":00"] || distMap[timeKey];
                        if (distItem) {
                            lastDistItem = distItem;
                        }

                        if (lastDistItem) {
                            avg_pct.push(lastDistItem.avg_pct);
                            downlimit_num.push(lastDistItem.downlimit_num);
                            up_num.push(lastDistItem.up_num);
                            down_num.push(lastDistItem.down_num);
                            zb_num.push(lastDistItem.zb_num || 0);
                            zrzt_num.push(lastDistItem.uplimit_num || 0);
                            distributeTrendData.push(lastDistItem);
                        } else {
                            avg_pct.push(null);
                            downlimit_num.push(null);
                            up_num.push(null);
                            down_num.push(null);
                            zb_num.push(null);
                            zrzt_num.push(null);
                            distributeTrendData.push(null);
                        }
                    } else {
                        // Future time
                        pct.push(null);
                        pct_zrlb.push(null);
                        avg_pct.push(null);
                        downlimit_num.push(null);
                        up_num.push(null);
                        down_num.push(null);
                        zb_num.push(null);
                        zrzt_num.push(null);
                        distributeTrendData.push(null);
                    }
                });

                this.lastDataIndex = maxTimeIndex;

                return {
                    categoryData,
                    pct,
                    zb_num,
                    zrzt_num,
                    pct_zrlb,
                    avg_pct,
                    downlimit_num,
                    up_num,
                    down_num,
                    distributeTrendData
                }
            },

			// Group 1: Intraday Sentiment Line Chart configuration
			getSentimentOptions() {
				const isDark = this.theme === 'dark';
				const textColor = isDark ? '#aaaaaa' : '#666666';
				const splitLineColor = isDark ? '#2e384d' : '#eeeeee';

				return {
					title: {
						text: '日内情绪热度',
						subtext: '',
						left: 0,
						textStyle: { color: isDark ? '#ffffff' : '#333333', fontSize: 14 },
						subtextStyle: { color: textColor, fontSize: 11 }
					},
					tooltip: {
						trigger: 'axis',
						axisPointer: { type: 'cross' },
						formatter: (params) => {
							if (!params || params.length === 0) return '';
							const dataIndex = params[0].dataIndex;
							const time = params[0].name || '';
							
							// Update active scrubbing index decoupling via $nextTick
							if (dataIndex !== undefined && dataIndex >= 0 && dataIndex !== this.selectedIndex) {
								this.$nextTick(() => {
									this.selectedIndex = dataIndex;
								});
							}

							let mainPct = '-';
							let sensPct = '-';
							let zrztNum = '-';
							let zbNum = '-';
							let downlimitNum = '-';

							params.forEach(p => {
								if (p.seriesName === '主要情绪') mainPct = p.value !== undefined ? p.value : '-';
								else if (p.seriesName === '敏感情绪') sensPct = p.value !== undefined ? p.value : '-';
								else if (p.seriesName === '自然涨停') zrztNum = p.value !== undefined ? p.value : '-';
								else if (p.seriesName === '炸板') zbNum = p.value !== undefined ? p.value : '-';
								else if (p.seriesName === '跌停数') downlimitNum = p.value !== undefined ? p.value : '-';
							});

							const avgColor = parseFloat(mainPct) >= 0 ? '#ec0000' : '#00da3c';

							return `
								<div style="font-family: sans-serif; padding: 10px 14px; background: ${isDark ? 'rgba(21, 29, 48, 0.95)' : 'rgba(255,255,255,0.98)'}; border: 1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#e2e8f0'}; border-radius: 8px; color: ${isDark ? '#fff' : '#1e293b'}; box-shadow: 0 4px 15px rgba(0,0,0,0.15); min-width: 200px; font-size: 12px; line-height: 1.6; text-align: left;">
									<div style="font-weight: bold; font-size: 13px; margin-bottom: 6px; border-bottom: 1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1'}; padding-bottom: 4px; display: flex; justify-content: space-between; align-items: center;">
										<span>${time}</span>
										<span style="font-size: 10px; color: ${isDark ? '#aaa' : '#64748b'};">日内情绪统计</span>
									</div>
									<div>主要情绪: <strong style="color: ${avgColor};">${mainPct}%</strong></div>
									<div>敏感情绪: <strong style="color: #4472C5;">${sensPct}%</strong></div>
									<div style="border-top: 1px dashed ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}; margin: 4px 0; padding-top: 4px;">
										<div>自然涨停: <strong style="color: #ef4444;">${zrztNum}只</strong></div>
										<div>炸板数: <strong style="color: #eab308;">${zbNum}只</strong></div>
										<div>跌停数: <strong style="color: #10b981;">${downlimitNum}只</strong></div>
									</div>
								</div>
							`;
						}
					},
					visualMap: [{
						type: 'piecewise',
						show: false,
						seriesIndex: [0],
						dimension: 1,
						pieces: [{
								min: parseFloat(0),
								max: parseFloat(1),
								color: upColor
							},
							{
								min: parseFloat(1.01),
								max: parseFloat(10),
								color: upBorderColor
							},
							{
								min: parseFloat(-2),
								max: parseFloat(0),
								color: downColor
							},
							{
								min: parseFloat(-10),
								max: parseFloat(-2),
								color: downBorderColor
							},
						],
						outOfRange: {
							symbol: 'rect',
							symbolSize: [1, 1],
							color: upBorderColor,
						}
					},
					{
						type: 'piecewise',
						show: false,
						seriesIndex: [1],
						dimension: 1,
						pieces: [{
								min: parseFloat(0),
								max: parseFloat(5),
								color: upColor
							},
							{
								min: parseFloat(5),
								max: parseFloat(10),
								color: upBorderColor
							},
							{
								min: parseFloat(-4),
								max: parseFloat(0),
								color: downColor
							},
							{
								min: parseFloat(-10),
								max: parseFloat(-4),
								color: downBorderColor
							},
						],
					}],
					toolbox: { feature: { saveAsImage: {} } },
					legend: {
						top: 5,
						right: 40,
						data: ['主要情绪', '敏感情绪', '自然涨停', '炸板', '跌停数'],
						textStyle: { color: textColor, fontSize: 10 }
					},
					color: ['#4472C5', '#888888', '#ef4444', '#eab308', '#10b981'],
					xAxis: [{
						type: 'category',
						data: this.categoryData,
						scale: true,
						boundaryGap: false,
						axisLine: { lineStyle: { color: isDark ? '#555555' : '#cccccc' } },
						axisLabel: { fontSize: 10, color: textColor }
					}],
					yAxis: [
						{
							name: '个股数量',
							type: 'value',
							scale: true,
							position: 'left',
							min: 0,
							max: (val) => val.max < 100 ? 100 : val.max,
							axisLabel: { formatter: '{value}只', color: textColor },
							axisLine: { lineStyle: { color: isDark ? '#555555' : '#cccccc' } },
							splitLine: { show: true, lineStyle: { color: splitLineColor } },
							nameTextStyle: { color: textColor }
						},
						{
							name: '情绪',
							type: 'value',
							scale: true,
							position: 'right',
							min: (val) => val.min > -4 ? -4 : val.min,
							max: (val) => val.max < 6 ? 6 : val.max,
							axisLabel: { formatter: '{value}%', color: textColor },
							axisLine: { lineStyle: { color: isDark ? '#555555' : '#cccccc' } },
							splitLine: { show: false }
						}
					],
					dataZoom: [{
						show: true,
						type: 'slider',
						y: '90%',
						start: this.zoom_start,
						end: this.zoom_end,
						textStyle: { color: textColor }
					}],
					series: [
						{
							name: '主要情绪',
							data: this.pct,
							type: 'line',
							symbol: 'circle',
							showSymbol: false,
							yAxisIndex: 1,
							lineStyle: { width: 3 },
							markLine: {
								data: [
									{ lineStyle: { width: 1, type: "solid", color: upBorderColor }, label: { formatter: "沸腾" }, yAxis: 3 },
									{ lineStyle: { width: 1, type: "dotted", color: upColor }, label: { formatter: "活跃" }, yAxis: 1 },
									{ lineStyle: { type: "dashed", color: "#AAA" }, label: { formatter: "及格" }, yAxis: 0 },
									{ lineStyle: { type: "dashed", color: downBorderColor }, label: { formatter: "冰凉" }, yAxis: -2 },
									{ lineStyle: { width: 1, type: "solid", color: downBorderColor }, label: { formatter: "冰点" }, yAxis: -4 }
								]
							}
						},
						{
							name: '敏感情绪',
							data: this.pct_zrlb,
							type: 'line',
							symbol: 'circle',
							showSymbol: false,
							yAxisIndex: 1,
							lineStyle: { width: 2, opacity: 0.6 }
						},
						{
							name: '自然涨停',
							data: this.zrzt_num,
							type: 'line',
							symbol: 'circle',
							showSymbol: false,
							yAxisIndex: 0,
							itemStyle: { color: '#ef4444' },
							lineStyle: { width: 1.5, color: '#ef4444', type: 'dashed' }
						},
						{
							name: '炸板',
							data: this.zb_num,
							type: 'line',
							symbol: 'circle',
							showSymbol: false,
							yAxisIndex: 0,
							itemStyle: { color: '#eab308' },
							lineStyle: { width: 1.5, color: '#eab308', type: 'dashed' }
						},
						{
							name: '跌停数',
							data: this.downlimit_num,
							type: 'line',
							symbol: 'circle',
							showSymbol: false,
							yAxisIndex: 0,
							itemStyle: { color: '#10b981' },
							lineStyle: { width: 1.5, color: '#10b981', type: 'dashed' }
						}
					]
				}
			},

			// Group 2: Price/Rise & Fall distribution Bar Chart configuration
			getDistributionOptions() {
				const isDark = this.theme === 'dark';
				const textColor = isDark ? '#aaaaaa' : '#666666';
				const splitLineColor = isDark ? '#2e384d' : '#eeeeee';

				// Fetch details at current selected index
				const dist = (this.distributeTrendData && this.distributeTrendData[this.selectedIndex]) || {};
				
				const v_uplimit = dist.fb_num !== undefined && dist.fb_num !== null ? dist.fb_num : 0;
				const v_gt10_20 = dist.gt_10_20_num !== undefined && dist.gt_10_20_num !== null ? dist.gt_10_20_num : 0;
				const v_gt7_10 = dist.gt_7_10_num !== undefined && dist.gt_7_10_num !== null ? dist.gt_7_10_num : 0;
				const v_gt5_7 = dist.gt_5_7_num !== undefined && dist.gt_5_7_num !== null ? dist.gt_5_7_num : 0;
				const v_gt2_5 = dist.gt_2_5_num !== undefined && dist.gt_2_5_num !== null ? dist.gt_2_5_num : 0;
				const v_gt0_2 = dist.gt_0_2_num !== undefined && dist.gt_0_2_num !== null ? dist.gt_0_2_num : 0;
				const v_flat = dist.eq_0_num !== undefined && dist.eq_0_num !== null ? dist.eq_0_num : 0;
				const v_lt0_2 = dist.lt_0_2_num !== undefined && dist.lt_0_2_num !== null ? dist.lt_0_2_num : 0;
				const v_lt2_5 = dist.lt_2_5_num !== undefined && dist.lt_2_5_num !== null ? dist.lt_2_5_num : 0;
				const v_lt5_7 = dist.lt_5_7_num !== undefined && dist.lt_5_7_num !== null ? dist.lt_5_7_num : 0;
				const v_lt7_10 = dist.lt_7_10_num !== undefined && dist.lt_7_10_num !== null ? dist.lt_7_10_num : 0;
				const v_lt10_20 = dist.lt_10_20_num !== undefined && dist.lt_10_20_num !== null ? dist.lt_10_20_num : 0;
				const v_downlimit = dist.downlimit_num !== undefined && dist.downlimit_num !== null ? dist.downlimit_num : 0;

				// Map red/green styles per Chinese stock custom palette (uplimit leftmost, downlimit rightmost)
				const barData = [
					{ value: v_uplimit, itemStyle: { color: '#ec0000', borderRadius: [4, 4, 0, 0] } },
					{ value: v_gt10_20, itemStyle: { color: '#dc2626', borderRadius: [4, 4, 0, 0] } },
					{ value: v_gt7_10, itemStyle: { color: '#ef4444', borderRadius: [4, 4, 0, 0] } },
					{ value: v_gt5_7, itemStyle: { color: '#f87171', borderRadius: [4, 4, 0, 0] } },
					{ value: v_gt2_5, itemStyle: { color: '#fca5a5', borderRadius: [4, 4, 0, 0] } },
					{ value: v_gt0_2, itemStyle: { color: '#fee2e2', borderRadius: [4, 4, 0, 0] } },
					{ value: v_flat, itemStyle: { color: isDark ? '#4b5563' : '#cbd5e1', borderRadius: [4, 4, 0, 0] } },
					{ value: v_lt0_2, itemStyle: { color: '#d1fae5', borderRadius: [4, 4, 0, 0] } },
					{ value: v_lt2_5, itemStyle: { color: '#a7f3d0', borderRadius: [4, 4, 0, 0] } },
					{ value: v_lt5_7, itemStyle: { color: '#34d399', borderRadius: [4, 4, 0, 0] } },
					{ value: v_lt7_10, itemStyle: { color: '#10b981', borderRadius: [4, 4, 0, 0] } },
					{ value: v_lt10_20, itemStyle: { color: '#059669', borderRadius: [4, 4, 0, 0] } },
					{ value: v_downlimit, itemStyle: { color: '#00da3c', borderRadius: [4, 4, 0, 0] } }
				];

				return {
					title: {
						text: `市场涨跌分布 (${this.currentTime || '-'})`,
						subtext: '',
						left: 0,
						textStyle: { color: isDark ? '#ffffff' : '#333333', fontSize: 14 },
						subtextStyle: { color: textColor, fontSize: 11 }
					},
					tooltip: {
						trigger: 'axis',
						axisPointer: { type: 'shadow' },
						formatter: (params) => {
							if (!params || params.length === 0) return '';
							const name = params[0].name;
							const val = params[0].value;
							return `<div style="font-size: 12px; padding: 4px 8px;"><strong>${name}</strong> : <span style="color: #ff9900; font-weight: bold;">${val}只</span></div>`;
						}
					},
					grid: { top: 70, bottom: 40, left: 50, right: 30 },
					xAxis: {
						type: 'category',
						data: ['涨停', '10~20%', '7~10%', '5~7%', '2~5%', '0~2%', '平盘', '-0~2%', '-2~5%', '-5~7%', '-7~10%', '-10~20%', '跌停'],
						axisLine: { lineStyle: { color: isDark ? '#555555' : '#cccccc' } },
						axisLabel: { fontSize: 11, color: isDark ? '#bbbbbb' : '#333333', fontWeight: 'bold' }
					},
					yAxis: {
						type: 'value',
						name: '个股只数',
						axisLabel: { formatter: '{value}只', color: textColor },
						axisLine: { lineStyle: { color: isDark ? '#555555' : '#cccccc' } },
						splitLine: { show: true, lineStyle: { color: splitLineColor } },
						nameTextStyle: { color: textColor }
					},
					series: [{
						name: '分布家数',
						type: 'bar',
						barWidth: '55%',
						data: barData,
						label: {
							show: true,
							position: 'top',
							fontSize: 10,
							fontWeight: 'bold',
							color: isDark ? '#ffffff' : '#333333'
						}
					}]
				}
			},

			// Group 3: Rise/Fall stock counts trend curves
			getCountsOptions() {
				const isDark = this.theme === 'dark';
				const textColor = isDark ? '#aaaaaa' : '#666666';
				const splitLineColor = isDark ? '#2e384d' : '#eeeeee';

				return {
					title: {
						text: '日内涨跌数与均涨幅趋势',
						subtext: '',
						left: 0,
						textStyle: { color: isDark ? '#ffffff' : '#333333', fontSize: 14 },
						subtextStyle: { color: textColor, fontSize: 11 }
					},
					tooltip: {
						trigger: 'axis',
						axisPointer: { type: 'cross' },
						formatter: (params) => {
							if (!params || params.length === 0) return '';
							const dataIndex = params[0].dataIndex;
							const time = params[0].name || '';

							if (dataIndex !== undefined && dataIndex >= 0 && dataIndex !== this.selectedIndex) {
								this.$nextTick(() => {
									this.selectedIndex = dataIndex;
								});
							}

							let upNum = '-';
							let downNum = '-';
							let avgPct = '-';

							params.forEach(p => {
								if (p.seriesName === '上涨数') upNum = p.value !== undefined ? p.value : '-';
								else if (p.seriesName === '下跌数') downNum = p.value !== undefined ? p.value : '-';
								else if (p.seriesName === '均涨幅') avgPct = p.value !== undefined ? p.value : '-';
							});

							const avgColor = parseFloat(avgPct) >= 0 ? '#ef4444' : '#10b981';

							return `
								<div style="font-family: sans-serif; padding: 10px 14px; background: ${isDark ? 'rgba(21, 29, 48, 0.95)' : 'rgba(255,255,255,0.98)'}; border: 1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#e2e8f0'}; border-radius: 8px; color: ${isDark ? '#fff' : '#1e293b'}; box-shadow: 0 4px 15px rgba(0,0,0,0.15); min-width: 160px; font-size: 12px; line-height: 1.6; text-align: left;">
									<div style="font-weight: bold; font-size: 13px; margin-bottom: 6px; border-bottom: 1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1'}; padding-bottom: 4px; display: flex; justify-content: space-between; align-items: center;">
										<span>${time}</span>
										<span style="font-size: 10px; color: ${isDark ? '#aaa' : '#64748b'};">多空与收益</span>
									</div>
									<div>上涨只数: <strong style="color: #ef4444;">${upNum}只</strong></div>
									<div>下跌只数: <strong style="color: #10b981;">${downNum}只</strong></div>
									<div style="border-top: 1px dashed ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}; margin: 4px 0; padding-top: 4px;">
										全场均涨幅: <strong style="color: ${avgColor};">${avgPct}%</strong>
									</div>
								</div>
							`;
						}
					},
					toolbox: { feature: { saveAsImage: {} } },
					legend: {
						top: 5,
						right: 40,
						data: ['上涨数', '下跌数', '均涨幅'],
						textStyle: { color: textColor, fontSize: 10 }
					},
					visualMap: [{
						type: 'piecewise',
						show: false,
						seriesIndex: [2],
						dimension: 1,
						pieces: [
							{
								min: 0.0001,
								max: 100,
								color: '#ef4444' // Red when > 0
							},
							{
								min: -100,
								max: 0,
								color: '#10b981' // Green when <= 0
							}
						]
					}],
					color: ['#ef4444', '#10b981', '#ff9900'],
					xAxis: [{
						type: 'category',
						data: this.categoryData,
						scale: true,
						boundaryGap: false,
						axisLine: { lineStyle: { color: isDark ? '#555555' : '#cccccc' } },
						axisLabel: { fontSize: 10, color: textColor }
					}],
					yAxis: [
						{
							name: '均涨幅',
							type: 'value',
							scale: true,
							position: 'left',
							axisLabel: { formatter: '{value}%', color: textColor },
							axisLine: { lineStyle: { color: isDark ? '#555555' : '#cccccc' } },
							splitLine: { show: true, lineStyle: { color: splitLineColor } },
							nameTextStyle: { color: textColor }
						},
						{
							name: '只数',
							type: 'value',
							scale: true,
							position: 'right',
							axisLabel: { formatter: '{value}只', color: textColor },
							axisLine: { lineStyle: { color: isDark ? '#555555' : '#cccccc' } },
							splitLine: { show: false }
						}
					],
					dataZoom: [{
						show: true,
						type: 'slider',
						y: '90%',
						start: this.zoom_start,
						end: this.zoom_end,
						textStyle: { color: textColor }
					}],
					series: [
						{
							name: '上涨数',
							data: this.up_num,
							type: 'line',
							symbol: 'circle',
							showSymbol: false,
							yAxisIndex: 1,
							lineStyle: { width: 2 }
						},
						{
							name: '下跌数',
							data: this.down_num,
							type: 'line',
							symbol: 'circle',
							showSymbol: false,
							yAxisIndex: 1,
							lineStyle: { width: 2 }
						},
						{
							name: '均涨幅',
							data: this.avg_pct,
							type: 'line',
							symbol: 'circle',
							showSymbol: false,
							yAxisIndex: 0,
							lineStyle: { width: 1, type: 'dashed' }
						}
					]
				}
			}
		},

		created(){ this.onThemeChanged = (e)=>{ const t = e && e.detail && e.detail.theme ? e.detail.theme : (localStorage.getItem('site.theme')||'light'); this.theme = t } },
		beforeDestroy() {
			window.removeEventListener('site-theme-changed', this.onThemeChanged)
			clearInterval(this.timer);
			this.timer = null;
			this.stopPlay();
		}
	}
</script>

<style scoped>
/* Core layout container */
.sentiment-layout-container {
	display: flex;
	flex-direction: row;
	gap: 20px;
	width: 100%;
	margin-top: 10px;
}

/* Sidebar structure */
.sentiment-sidebar {
	width: 190px;
	flex-shrink: 0;
	display: flex;
	flex-direction: column;
	gap: 12px;
}

/* Main chart canvas block */
.sentiment-chart-area {
	flex-grow: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
}

.chart_hot_trend {
	width: 100% !important;
	height: 400px !important;
}

/* Premium Card Styles */
.trend-tab-card {
	padding: 10px 12px;
	border-radius: 10px;
	border: 1px solid rgba(0, 0, 0, 0.08);
	background: rgba(255, 255, 255, 0.6);
	backdrop-filter: blur(10px);
	cursor: pointer;
	transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.02);
	position: relative;
	overflow: hidden;
}

.trend-tab-card::before {
	content: '';
	position: absolute;
	left: 0;
	top: 0;
	bottom: 0;
	width: 4px;
	background: transparent;
	transition: background-color 0.3s ease;
}

.trend-tab-card:hover {
	transform: translateY(-2px);
	box-shadow: 0 8px 16px rgba(0, 0, 0, 0.06);
	border-color: rgba(239, 68, 68, 0.25);
}

.trend-tab-card.active {
	background: rgba(255, 255, 255, 0.95);
	border-color: rgba(239, 68, 68, 0.4);
	box-shadow: 0 8px 24px rgba(239, 68, 68, 0.1);
}

.trend-tab-card.active::before {
	background: #ef4444;
}

/* Tab contents */
.trend-tab-card-header {
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 8px;
}

.trend-tab-icon {
	font-size: 16px;
}

.trend-tab-title {
	font-weight: 700;
	font-size: 13px;
	color: #1e293b;
	letter-spacing: 0.5px;
}

.trend-tab-card-body {
	font-size: 12px;
	color: #475569;
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 2px;
}

.trend-tab-label {
	color: #64748b;
	margin-right: 4px;
}

/* Dark mode theme variables */
.site-dark-mode .trend-tab-card {
	border-color: rgba(255, 255, 255, 0.06);
	background: rgba(30, 41, 59, 0.4);
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
}

.site-dark-mode .trend-tab-card:hover {
	border-color: rgba(239, 68, 68, 0.5);
	box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
}

.site-dark-mode .trend-tab-card.active {
	background: rgba(30, 41, 59, 0.85);
	border-color: rgba(239, 68, 68, 0.6);
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.site-dark-mode .trend-tab-title {
	color: #f1f5f9;
}

.site-dark-mode .trend-tab-card-body {
	color: #cbd5e1;
}

.site-dark-mode .trend-tab-label {
	color: #94a3b8;
}

/* Premium Custom Scrubber Slider styling */
.custom-slider {
	-webkit-appearance: none;
	appearance: none;
	width: 100%;
	height: 8px;
	border-radius: 4px;
	background: #e2e8f0;
	outline: none;
	transition: background 0.3s;
	margin: 8px 0;
}

.site-dark-mode .custom-slider {
	background: #334155;
}

.custom-slider::-webkit-slider-thumb {
	-webkit-appearance: none;
	appearance: none;
	width: 18px;
	height: 18px;
	border-radius: 50%;
	background: #ef4444;
	cursor: pointer;
	box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
	transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.custom-slider::-webkit-slider-thumb:hover {
	transform: scale(1.25);
	background: #dc2626;
}

.sentiment-scrubber-container {
	height: 55px;
	margin-top: 10px;
	box-sizing: border-box;
}

/* Responsive grid setup for tablets & mobile devices */
@media (max-width: 768px) {
	.sentiment-layout-container {
		flex-direction: column;
		gap: 15px;
	}
	.sentiment-sidebar {
		width: 100%;
		flex-direction: row;
		overflow-x: auto;
		padding-bottom: 5px;
	}
	.trend-tab-card {
		flex: 1;
		min-width: 180px;
	}
}
</style>
